import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandInfo } from '@/mocks/movies';

const pageTitle = '关于琪琪影院 - 品牌介绍 口碑评价 联系方式 | 电影大全免费在线看';
const pageDescription = '琪琪影院品牌介绍——国内领先免费电影平台，800万+用户信赖之选，50000+电影资源每日更新，高清蓝光画质流畅不缓冲。了解琪琪影院口碑与联系方式。';
const pageKeywords = '琪琪影院,关于我们,品牌介绍,电影大全,免费在线电影,联系方式,口碑评价';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormState('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9n3bdsit35ii3jdlnvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.meta as Record<string, string>)?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormState('error');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brandInfo.name,
    url: 'https://qiqicinema.com',
    description: brandInfo.description,
    foundingDate: brandInfo.founded,
    email: brandInfo.contact.email,
    telephone: `+86-${brandInfo.contact.phone}`,
    address: {
      '@type': 'PostalAddress',
      streetAddress: brandInfo.contact.address.split('SOHO')[0] + 'SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qiqicinema.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalUrl="https://qiqicinema.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto max-w-[1400px] px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="bg-background-100 py-12 md:py-16">
            <div className="mx-auto max-w-[1000px] px-4 md:px-6 text-center">
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-900 mb-4">
                关于琪琪影院
              </h1>
              <p className="text-sm text-foreground-500 mb-10 max-w-3xl mx-auto leading-relaxed">
                {brandInfo.description}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
                {brandInfo.reputation.map((item) => (
                  <div key={item.label} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                      <i className={`${item.icon} text-lg text-accent-900`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1 whitespace-nowrap">
                      {item.value}
                    </div>
                    <div className="text-xs text-foreground-500 whitespace-nowrap">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-background-50 py-12 md:py-16">
            <div className="mx-auto max-w-[1000px] px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                <div>
                  <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-6">
                    <a href="#contact" id="contact">联系我们</a>
                  </h2>

                  {formState === 'success' ? (
                    <div className="rounded-lg p-6 bg-accent-100 border border-accent-300">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-500">
                          <i className="ri-check-line text-background-50"></i>
                        </div>
                        <span className="font-heading font-semibold text-accent-900">提交成功</span>
                      </div>
                      <p className="text-sm text-accent-800">感谢您的留言！我们会尽快通过邮件与您联系。</p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleContactSubmit}
                      className="space-y-4"
                      data-readdy-form=""
                      id="about-contact-form"
                    >
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          您的姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        />
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        className="form-anti-spam"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                      {formState === 'error' && (
                        <div className="text-sm text-primary-600 bg-primary-100/70 px-4 py-2 rounded-lg">
                          {formError}
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                      >
                        {formState === 'submitting' ? '提交中...' : '发送留言'}
                      </button>
                    </form>
                  )}
                </div>

                <div>
                  <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-6">
                    联系方式
                  </h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-0.5">电子邮箱</div>
                        <div className="text-sm text-foreground-500">{brandInfo.contact.email}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-phone-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-0.5">客服电话</div>
                        <div className="text-sm text-foreground-500">{brandInfo.contact.phone}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-wechat-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-0.5">微信公众号</div>
                        <div className="text-sm text-foreground-500">{brandInfo.contact.wechat}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-weibo-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-0.5">官方微博</div>
                        <div className="text-sm text-foreground-500">{brandInfo.contact.weibo}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800 mb-0.5">公司地址</div>
                        <div className="text-sm text-foreground-500">{brandInfo.contact.address}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}